import React from "react";
import { Rocket, Briefcase, TrendingUp, Target, Zap, Phone, Image as ImageIcon } from "lucide-react";
import Header from "../components/Header";

export default function SalesPage() {
  return (
    <div className="min-h-screen tech-bg text-white overflow-x-hidden relative pb-32">
      <Header />

      {/* HERO */}
      <section className="relative z-10 px-4 md:px-8 pt-10 md:pt-14 pb-14">
        <div className="hero-glow" />
        <div className="max-w-6xl mx-auto relative text-center">
          <p className="text-[#C4FF3D] uppercase tracking-[0.3em] text-xs md:text-sm font-semibold mb-4">
            The AI Empire Workshop &middot; 2-Day Intensive
          </p>
          <h1 className="display-font text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6">
            <span className="text-white">The Proven Path To Building An </span>
            <span className="lime-box">AI-First Business</span>
            <span className="text-white"> That Prints Attention &amp; Revenue On Autopilot.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-3xl mx-auto mb-10">
            Taught by <span className="text-white font-semibold">Nawaz Shaikh</span> &mdash; India&apos;s fastest growing AI expert. A hands-on intensive for founders, coaches &amp; creators ready to install the exact AI &amp; content systems behind multi-crore brands.
          </p>

          {/* Placeholder media frame (intentionally blank, no mentor image) */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute -inset-6 bg-[radial-gradient(circle,rgba(196,255,61,0.18),transparent_60%)] blur-3xl" />
            <div className="relative aspect-video w-full rounded-2xl border border-dashed border-[rgba(196,255,61,0.35)] bg-[#0e1410] flex items-center justify-center overflow-hidden">
              <div className="text-center px-6">
                <div className="w-16 h-16 mx-auto rounded-2xl border border-[rgba(196,255,61,0.25)] bg-[#0a0f0a] flex items-center justify-center mb-4">
                  <ImageIcon className="w-7 h-7 text-[#C4FF3D]/80" />
                </div>
                <p className="text-white/45 text-sm uppercase tracking-[0.25em] font-semibold">
                  Workshop Trailer &middot; Coming Soon
                </p>
              </div>
              {/* subtle corner accents */}
              <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[#C4FF3D]/40" />
              <div className="absolute top-3 right-3 w-6 h-6 border-t border-r border-[#C4FF3D]/40" />
              <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#C4FF3D]/40" />
              <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[#C4FF3D]/40" />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#invest" className="btn-cta">Pay Token &amp; Secure Your Seat</a>
            <a href="#invest" className="px-7 py-4 rounded-full border border-[rgba(196,255,61,0.4)] text-white font-bold hover:border-[#C4FF3D] hover:text-[#C4FF3D] transition">
              Pay Full &amp; Confirm Your Seat
            </a>
          </div>
        </div>
      </section>

      {/* 3 BENEFITS */}
      <section className="relative z-10 px-4 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="display-font text-center text-3xl md:text-5xl font-extrabold mb-4 leading-tight">
            Build An AI Empire,{" "}
            <span className="lime-box">Exponentially.</span>
          </h2>
          <p className="text-center text-white/65 max-w-2xl mx-auto mb-14">
            Three transformations you&apos;ll walk away with by the end of the 2-day intensive.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <Benefit
              icon={<TrendingUp className="w-7 h-7" />}
              title="LEARN & SCALE WITH AI"
              desc="Install the exact AI content stack used by 8-figure creators &mdash; so you can systematically uncover what's working in your niche and 10x your output without burning out."
            />
            <Benefit
              icon={<Target className="w-7 h-7" />}
              title="REDEFINE YOUR FUTURE"
              desc="Know exactly where your brand actually is &mdash; not where you think it is. Rebuild a content + monetization map that compounds, no matter how the algorithm changes."
              highlighted
            />
            <Benefit
              icon={<Rocket className="w-7 h-7" />}
              title="MAXIMIZE & MONETIZE"
              desc="Engineer every piece of attention into a revenue-generating asset. Maximize systems, optimize operations &mdash; and turn AI from a feature into your entire business model."
            />
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#invest" className="btn-cta">Pay Token &amp; Secure Your Seat</a>
            <a href="#invest" className="px-7 py-4 rounded-full border border-[rgba(196,255,61,0.4)] text-white font-bold hover:border-[#C4FF3D] hover:text-[#C4FF3D] transition">
              Pay Full &amp; Confirm Your Seat
            </a>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="relative z-10 px-4 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-[#C4FF3D] uppercase tracking-[0.3em] text-xs md:text-sm font-semibold mb-3">
            What's Inside The Room
          </p>
          <h2 className="display-font text-center text-3xl md:text-5xl font-extrabold mb-12 leading-tight">
            Everything You Need To <span className="lime-box">Win With AI</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUSIONS.map((it) => (
              <div key={it.title} className="feature-card p-6">
                <div className="w-12 h-12 rounded-xl bg-[#0a0f0a] border border-[rgba(196,255,61,0.25)] flex items-center justify-center text-[#C4FF3D] mb-4">
                  {it.icon}
                </div>
                <h3 className="text-white text-lg font-bold mb-2">{it.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section id="invest" className="relative z-10 px-4 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <div className="absolute -inset-1 rounded-[28px] bg-[conic-gradient(from_var(--angle),transparent_0deg,rgba(196,255,61,0.6)_60deg,transparent_120deg,transparent_240deg,rgba(196,255,61,0.35)_300deg,transparent_360deg)] opacity-70 blur-sm vip-spin" />
            <div className="relative rounded-[28px] border border-[rgba(196,255,61,0.35)] bg-gradient-to-b from-[#0c130c] to-[#0a0f0a] overflow-hidden">
              <div className="px-5 md:px-7 py-3 border-b border-dashed border-[rgba(196,255,61,0.2)] bg-[rgba(196,255,61,0.04)] text-center">
                <span className="text-[10px] md:text-xs uppercase tracking-[0.18em] font-bold text-[#C4FF3D]">
                  Your Investment
                </span>
              </div>
              <div className="p-7 md:p-10 text-center">
                <div className="flex items-center justify-center gap-3 mb-2 text-white/55">
                  <Briefcase className="w-5 h-5 text-[#C4FF3D]" />
                  <span className="text-sm md:text-base">Regular Price</span>
                </div>
                <p className="relative inline-block display-font text-2xl md:text-3xl font-bold text-white/55 mb-6">
                  ₹1,00,000 + 18% GST
                  <span className="absolute left-0 right-0 top-1/2 h-[3px] bg-red-500 rotate-[-6deg]" />
                </p>

                <div className="flex items-center justify-center gap-3 mb-2 text-white/75">
                  <Rocket className="w-5 h-5 text-[#C4FF3D]" />
                  <span className="text-sm md:text-base uppercase tracking-[0.18em] font-semibold">Offer Price</span>
                </div>
                <p className="display-font text-5xl md:text-6xl font-extrabold text-[#C4FF3D] mb-2 free-pulse">
                  ₹24,999
                </p>
                <p className="text-white/55 text-sm mb-8">+ 18% GST</p>

                <div className="flex flex-col gap-3">
                  <a href="https://rzp.io/rzp/FDfaiUN" className="cta-vip group w-full">
                    <span className="cta-vip-inner">
                      <Zap className="w-5 h-5" fill="currentColor" />
                      <span>Pay Full &amp; Confirm Your Seat</span>
                      <span className="cta-arrow">→</span>
                    </span>
                  </a>
                  <a
                    href="https://rzp.io/rzp/FDfaiUN"
                    className="w-full py-4 rounded-2xl border border-dashed border-[rgba(196,255,61,0.5)] text-[#C4FF3D] font-bold hover:bg-[rgba(196,255,61,0.06)] transition"
                  >
                    Pay Token (₹3,000) &amp; Secure Your Seat
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WORKSHOP CTA HEADLINE */}
      <section className="relative z-10 px-4 py-16 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C4FF3D] uppercase tracking-[0.3em] text-xs md:text-sm font-semibold mb-4">
            Join Nawaz Shaikh For
          </p>
          <h2 className="display-font text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-5">
            <span className="lime-box">The AI Empire</span>{" "}
            <span className="text-white">Workshop</span>
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Transform your business 360° with AI and become the category leader in your niche.
          </p>
        </div>
      </section>

      {/* SUPPORT */}
      <section className="relative z-10 px-4 py-12">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-white/55 uppercase tracking-[0.25em] text-xs font-semibold mb-5">
            For Any Help Related To Tickets Or Support
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {SUPPORT_PHONES.map((p) => (
              <a
                key={p}
                href={`tel:${p.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[rgba(196,255,61,0.25)] bg-[#0e1410] text-white text-sm font-semibold hover:border-[#C4FF3D] hover:text-[#C4FF3D] transition"
              >
                <Phone className="w-4 h-4 text-[#C4FF3D]" />
                {p}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER LINKS */}
      <footer className="relative z-10 px-4 py-10 mt-6 border-t border-dashed border-[rgba(196,255,61,0.15)]">
        <div className="max-w-5xl mx-auto text-center text-white/45 text-xs md:text-sm space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5">
            <a href="#" className="hover:text-white">About Us</a>
            <span className="text-white/20">|</span>
            <a href="#" className="hover:text-white">Contact Us</a>
            <span className="text-white/20">|</span>
            <a href="#" className="hover:text-white">Refund Policy</a>
          </div>
          <p>&copy; ViralX 2026 &middot; ALL RIGHTS RESERVED</p>
        </div>
      </footer>

      {/* STICKY FOOTER BAR */}
      <div className="float-bar">
        <div className="relative max-w-7xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between gap-3 md:gap-4">
          <div className="min-w-0">
            <p className="text-white text-sm md:text-base font-semibold leading-tight">
              Offer Price:{" "}
              <span className="text-[#C4FF3D] font-extrabold">₹24,999</span>{" "}
              <span className="text-white/55 text-xs md:text-sm">+ 18% GST</span>
            </p>
            <p className="text-white/55 text-[11px] md:text-xs mt-0.5 line-through">
              Regular ₹1,00,000
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <a
              href="#invest"
              className="hidden sm:inline-flex px-4 py-2.5 rounded-full border border-dashed border-[rgba(196,255,61,0.5)] text-[#C4FF3D] text-sm font-bold hover:bg-[rgba(196,255,61,0.06)] transition"
            >
              Pay Token (₹3,000)
            </a>
            <a href="#invest" className="btn-cta-sm text-xs md:text-sm">
              Pay Full Amount
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Benefit({ icon, title, desc, highlighted }) {
  return (
    <div className={`feature-card p-7 text-center relative ${highlighted ? "border-[rgba(196,255,61,0.45)] shadow-[0_0_40px_rgba(196,255,61,0.1)]" : ""}`}>
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#0a0f0a] border border-[rgba(196,255,61,0.3)] flex items-center justify-center text-[#C4FF3D] mb-5">
        {icon}
      </div>
      <h3 className="text-white text-base md:text-lg font-extrabold tracking-wide mb-3">{title}</h3>
      <p className="text-white/65 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

const INCLUSIONS = [
  {
    icon: <Zap className="w-5 h-5" />,
    title: "2-Day Live Intensive",
    desc: "Hands-on workshop with Nawaz Shaikh, step-by-step frameworks, live demos and direct Q&A.",
  },
  {
    icon: <Target className="w-5 h-5" />,
    title: "AI Content Engine Stack",
    desc: "The exact tools, prompts and workflows used to publish 30+ pieces of content a day on autopilot.",
  },
  {
    icon: <TrendingUp className="w-5 h-5" />,
    title: "Monetization Blueprint",
    desc: "Turn views into clients with the funnel architecture used by ₹50Cr+ brands.",
  },
  {
    icon: <Rocket className="w-5 h-5" />,
    title: "DM & Lead Automation",
    desc: "Plug-and-play automations so inbound leads convert into calls and sales while you sleep.",
  },
  {
    icon: <Briefcase className="w-5 h-5" />,
    title: "Personal Brand Playbook",
    desc: "Position yourself as the category-leading authority in your niche &mdash; even if you're starting from zero.",
  },
  {
    icon: <Phone className="w-5 h-5" />,
    title: "Lifetime Community Access",
    desc: "Private alumni network for accountability, partnerships and ongoing implementation support.",
  },
];

const SUPPORT_PHONES = ["+91 70690 44474", "+91 70690 44473", "+91 70690 44467"];
